import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import collect from 'collect.js';
import { COUPON_TYPES, DropoffAction, ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import { Order, OrderData } from '../../services/exports/Interfaces';
import { OrderStoreInterface, StoreInterface } from '../types';
import { isTableSelectionActive, normaliseOrderFields, normaliseOrderMethod } from '../../utils/on-site';

const initialOrder: Partial<OrderData> = {
  method: ORDER_METHODS.PICKUP,
  customer_note: '',
  delivery_street_name: null,
  delivery_street_number: null,
  delivery_city: null,
  delivery_state: null,
  delivery_country: null,
  delivery_company_name: null,
  delivery_doorbell_name: null,
  delivery_zip_code: null,
  delivery_lat: null,
  delivery_lng: null,
  delivery_dropoff_action: DropoffAction.RingDoorBell,
  on_site_table: null,
  promo: '',
  coupon_details: null,
  cashback_points: null,
  tip: undefined,
  scheduled_for: null,
  line_items: [],
  marketing_consent: false,
};

const orderSlice = createSlice({
  name: REDUX_STORE_SLICES.ORDER,
  initialState: {
    cached_order: initialOrder,
    ongoing_orders: [],
  },
  reducers: {
    updateOrder: (state: OrderStoreInterface, action: PayloadAction<Partial<OrderData>>) => {
      // Orders from the backend are merged in here too (drafts, created and updated orders, payment
      // links), so their method and table are mapped to what the cart keeps.
      state.cached_order = {
        ...state.cached_order,
        ...normaliseOrderFields(action.payload, state.cached_order?.on_site_table),
      };
    },
    resetOrder: (state: OrderStoreInterface, action?: PayloadAction<Partial<OrderData>>) => {
      const keep = [
        'customer_first_name',
        'customer_last_name',
        'customer_email',
        'customer_phone_number',
        'delivery_street_name',
        'delivery_street_number',
        'delivery_city',
        'delivery_state',
        'delivery_country',
        'delivery_zip_code',
        'delivery_dropoff_action',
        'delivery_doorbell_name',
        'delivery_company_name',
        'delivery_lat',
        'delivery_lng',
        'is_receipt_required',
        'invoice_company_name',
        'invoice_first_name',
        'invoice_last_name',
        'invoice_address',
        'invoice_zip_code',
        'invoice_city',
        'invoice_tax_id',
        'invoice_email',
      ];

      // The table stays through the reset while the scan is still good (3 h, this restaurant): a
      // guest's next round at the table is a table order, not a pickup order placed without a
      // word. Once the visit ran out the cart goes to pickup; only a new scan chooses a table again.
      // A reset can also pass the table back (one that isn't about an order placed, e.g. the basket's).
      const method = state.cached_order?.method ?? ORDER_METHODS.PICKUP;
      const table = action?.payload?.on_site_table ?? state.cached_order?.on_site_table ?? null;
      const keepsTable = method === ORDER_METHODS.ON_SITE && isTableSelectionActive(table, table?.company_id);
      const data = {
        ...initialOrder,
        ...collect(state.cached_order).only(keep).items,
        method: method === ORDER_METHODS.ON_SITE && !keepsTable ? ORDER_METHODS.PICKUP : method,
        ...(keepsTable && { on_site_table: table }),
      };

      state.cached_order = {
        ...data,
        ...collect(action.payload).filter((value, key) => !data[key]).items,
      };
    },
    resetScheduledTime: (state: OrderStoreInterface, action?: PayloadAction<Partial<OrderData>>) => {
      state.cached_order = {
        ...state.cached_order,
        scheduled_for: action?.payload?.scheduled_for ?? null,
      };
    },
    addOngoingOrder: (state: OrderStoreInterface, action: PayloadAction<Order | null | undefined>) => {
      // A response that ended without a body (Firefox resolves a cut-off XHR as a success) is no order.
      // An unpaid order (e.g. an abandoned PayPal checkout) has no fulfillment yet, so nothing to track (SHOP-WEB-6N).
      if (!action.payload?.id || !action.payload.fulfillment) {
        return;
      }

      // Under the shop's method value; the table and its name snapshot stay as the backend sends them
      // (the order screen and the ongoing-order card read those, not a scan selection).
      const order = { ...action.payload, method: normaliseOrderMethod(action.payload.method) };
      const index = collect(state.ongoing_orders).search((item) => item.id === order.id);

      state.ongoing_orders = collect([...(state.ongoing_orders ?? [])])
        .when(
          index === false,
          (items) => items.prepend(order),
          (items) => items.put(index, order),
        )
        .toArray();
    },
    removeOngoingOrder: (state: OrderStoreInterface, action: PayloadAction<number>) => {
      state.ongoing_orders = collect(state.ongoing_orders).where('id', '!=', action.payload).toArray();
    },
  },
});

export const props = (state: OrderStoreInterface) => state;
export const selectOrderMethod = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order?.method;
export const selectCachedOrder = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order;
export const selectScheduledFor = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER].cached_order?.scheduled_for;
export const selectCouponType = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order?.coupon_type;
export const selectOngoingOrder = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER]?.ongoing_orders?.[0];
export const selectHasCachedOrderPromoCode = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER]?.cached_order?.coupon_type === COUPON_TYPES.PROMO_CODE;
// A points redemption is active: coupon_type is CASHBACK and points are actually being spent.
export const selectHasCachedOrderPointsRedemption = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER]?.cached_order?.coupon_type === COUPON_TYPES.CASHBACK &&
  (state[REDUX_STORE_SLICES.ORDER]?.cached_order?.cashback_points ?? 0) > 0;
export const selectPaymentProps = createSelector(
  (state) => state[REDUX_STORE_SLICES.ORDER]?.cached_order,
  (order: OrderData) => ({
    payment_method_id: order?.payment_method_id,
    payment_intent_id: order?.payment_intent_id,
    customer_phone_number: order?.customer_phone_number,
    customer_email: order?.customer_email,
    customer_first_name: order?.customer_first_name,
    customer_last_name: order?.customer_last_name,
    is_receipt_required: order?.is_receipt_required,
    invoice_company_name: order?.invoice_company_name,
    invoice_first_name: order?.invoice_first_name,
    invoice_last_name: order?.invoice_last_name,
    invoice_address: order?.invoice_address,
    invoice_zip_code: order?.invoice_zip_code,
    invoice_city: order?.invoice_city,
    invoice_tax_id: order?.invoice_tax_id,
    invoice_email: order?.invoice_email,
  }),
);

export default orderSlice;
